import React, { useState, useCallback } from 'react';
import { Scanner } from '@yudiel/react-qr-scanner';
import { useLang } from '../context/LanguageContext';
import api from '../utils/api';

export default function BarcodeScanner({ packagers, onPackagerSelected, onPackagerCreated }) {
  const { t } = useLang();
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState('');
  const [unknownCode, setUnknownCode] = useState(null);
  const [newPackagerName, setNewPackagerName] = useState('');
  const [creating, setCreating] = useState(false);

  const handleScan = useCallback((result) => {
    if (!result || !result.length) return;

    const code = result[0].rawValue.trim();
    if (!code) return;

    // Stop scanning
    setScanning(false);

    // Look up packager by barcode (packager_number)
    const found = packagers.find(
      p => p.packager_number.toLowerCase() === code.toLowerCase()
    );

    if (found) {
      onPackagerSelected(found.packager_number);
      setError('');
      setUnknownCode(null);
    } else {
      // Packager not found - show create dialog
      setUnknownCode(code);
      setError('');
    }
  }, [packagers, onPackagerSelected]);

  const handleCreatePackager = async () => {
    if (!newPackagerName.trim()) return;
    setCreating(true);
    try {
      await api.post('/admin/packagers', {
        packager_number: unknownCode,
        name: newPackagerName.trim()
      });
      // Notify parent to refresh packagers and select the new one
      onPackagerCreated(unknownCode);
      setUnknownCode(null);
      setNewPackagerName('');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create packager.');
    } finally {
      setCreating(false);
    }
  };

  const handleError = (err) => {
    console.error('Scanner error:', err);
    setError(t('camera_error'));
  };

  return (
    <div>
      {/* Scan button */}
      {!scanning && !unknownCode && (
        <button
          type="button"
          onClick={() => { setScanning(true); setError(''); }}
          className="w-full py-4 px-4 border-2 border-dashed border-orange-300 rounded-2xl bg-orange-50 text-orange-700 font-bold text-base active:bg-orange-100 transition-all touch-active flex items-center justify-center gap-3"
        >
          <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
          </svg>
          📷 {t('scan_barcode')}
        </button>
      )}

      {/* Camera scanner */}
      {scanning && (
        <div className="fade-in">
          <div className="relative rounded-2xl overflow-hidden border-2 border-orange-400 bg-black" style={{ maxHeight: '300px' }}>
            <Scanner
              onScan={handleScan}
              onError={handleError}
              constraints={{
                facingMode: 'environment'
              }}
              styles={{
                container: { borderRadius: '16px', overflow: 'hidden' },
                video: { borderRadius: '16px' }
              }}
              components={{
                audio: false,
                torch: true
              }}
            />
            {/* Overlay guide */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="border-2 border-white border-dashed rounded-lg opacity-50" style={{ width: '70%', height: '40%' }} />
            </div>
          </div>
          <p className="text-center text-sm text-gray-500 mt-2 font-medium">
            📷 {t('point_camera')}
          </p>
          <button
            type="button"
            onClick={() => setScanning(false)}
            className="w-full mt-3 py-3 px-4 bg-gray-100 text-gray-700 rounded-xl font-semibold text-sm active:bg-gray-200 transition-all touch-active"
          >
            ✕ {t('stop_scan')}
          </button>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="mt-3 p-3 bg-red-50 border border-red-200 text-red-700 rounded-xl text-sm font-medium fade-in">
          {error}
        </div>
      )}

      {/* Unknown packager dialog */}
      {unknownCode && (
        <div className="mt-3 p-5 bg-yellow-50 border-2 border-yellow-300 rounded-2xl fade-in">
          <div className="flex items-start gap-3">
            <span className="text-3xl">⚠️</span>
            <div className="flex-1">
              <h3 className="font-bold text-yellow-800 text-base">{t('packager_not_found')}</h3>
              <p className="text-sm text-yellow-700 mt-1">
                {t('packager_not_found_msg', { code: unknownCode })}
              </p>

              <div className="mt-4 space-y-3">
                <div>
                  <label className="block text-xs font-bold text-yellow-800 mb-1">{t('packager_number')}</label>
                  <input
                    type="text"
                    value={unknownCode}
                    disabled
                    className="w-full px-4 py-3 border-2 border-yellow-300 rounded-xl bg-yellow-100 text-yellow-800 font-bold"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-yellow-800 mb-1">{t('packager_name')}</label>
                  <input
                    type="text"
                    value={newPackagerName}
                    onChange={(e) => setNewPackagerName(e.target.value)}
                    placeholder={t('enter_packager_name')}
                    className="w-full px-4 py-3 border-2 border-yellow-300 rounded-xl focus:outline-none focus:border-yellow-500 bg-white"
                  />
                </div>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={handleCreatePackager}
                    disabled={!newPackagerName.trim() || creating}
                    className="flex-1 py-3 px-4 bg-green-500 text-white rounded-xl font-bold active:bg-green-600 disabled:opacity-50 transition-all touch-active"
                  >
                    {creating ? '...' : `✅ ${t('create_new_packager')}`}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setUnknownCode(null); setNewPackagerName(''); }}
                    className="py-3 px-4 bg-gray-200 text-gray-700 rounded-xl font-semibold active:bg-gray-300 transition-all touch-active"
                  >
                    {t('cancel')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Divider */}
      {!scanning && !unknownCode && (
        <p className="text-center text-xs text-gray-400 mt-2 font-medium">
          {t('or_select_manually')} ↓
        </p>
      )}
    </div>
  );
}
